import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router";
import { App } from "./App.tsx";
import { whenSignedOut } from "./lib/api.ts";
import { notifyError } from "./lib/notify.ts";
import { SIGN_IN_PATH, signInUrl } from "./lib/signin.ts";
import { reportError } from "./lib/report.ts";
import { retryDelay, shouldRetry } from "./lib/retry.ts";
import { forgetHome, restoreHome, watchHome } from "./lib/homeCache.ts";
// Served with the app rather than by Google: their stylesheet held the first
// paint on every cold start, from two more hosts than the server.
import "@fontsource-variable/archivo/wdth.css";
import "./styles.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A screen already showing data keeps it while the retries run; one
      // that has none says the server is unreachable as soon as a try fails.
      retry: shouldRetry,
      retryDelay,
      staleTime: 30_000,
      refetchOnWindowFocus: true,
    },
  },
});

// The home screen's last answers, on screen at once while fresh ones come.
restoreHome(queryClient, import.meta.env.VITE_BUILD_ID);
watchHome(queryClient, import.meta.env.VITE_BUILD_ID);

// Last line of defence. A rejected promise in a click handler is exactly how
// the set button failed silently on 27/09: say so, and log it.
window.addEventListener("unhandledrejection", (event) => notifyError("Erreur inattendue", event.reason));
window.addEventListener("error", (event) => reportError(event.error ?? event.message, { source: "window.error" }));

// Any request answered 401 lands here. Writes already sit in the queue.
whenSignedOut(() => {
  forgetHome();
  if (location.pathname !== SIGN_IN_PATH) location.assign(signInUrl(location.pathname + location.search));
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
